import React, { useEffect, useState, useMemo } from 'react'
import ResponsiveDrawer from '../Drawer/drawer'
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Notification from "../AlertNotification/Message";
import { getProductSizes } from '../../ReusableComponents/functions';
import { useParams, useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Grid from '@mui/material/Grid';
import Items from '../../ReusableComponents/Items';
import IconButtonWithBadge from '../../ReusableComponents/CartIcon';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useOrderDraft } from './OrderDraftContext';

const SelectSizeAndQty = () => {
    const drawerWidth = 240;
    const { productid, sku, stylefactory } = useParams(); // productid holds step, stylefactory holds product name
    const navigate = useNavigate();

    const { draft, updateCartItemQty, clearCart } = useOrderDraft();

    // Parse the step number from URL or default to '1'
    const defaultStep = useMemo(() => {
        const parsed = parseInt(productid, 10);
        return (!isNaN(parsed) && parsed >= 1 && parsed <= 10) ? String(parsed) : '1';
    }, [productid]);

    const [qty, setQty] = useState(defaultStep);
    const [incDec, setIncDec] = useState('increment');
    const [products, setProducts] = useState([]);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [notify, setNotify] = useState({
        isOpen: false,
        message: "",
        type: "",
    });

    const handleQtyChange = (event, newQty) => {
        if (newQty !== null) {
            setQty(newQty);
        }
    };

    const handleIncDecChange = (event, newIncDec) => {
        if (newIncDec !== null) {
            setIncDec(newIncDec);
        }
    };

    // Calculate current product's total pairs by summing quantities in draft.cartItems matching the active SKU
    const currentProductPairs = useMemo(() => {
        return draft.cartItems
            .filter(item => item.sku === sku)
            .reduce((sum, item) => sum + item.unitQuantity, 0);
    }, [draft.cartItems, sku]);

    // Calculate total pairs in entire cart for floating badge
    const totalCartPairs = useMemo(() => {
        return draft.cartItems.reduce((sum, item) => sum + item.unitQuantity, 0);
    }, [draft.cartItems]);

    // Initialize/sync size rows from selected product and match current draft quantities
    useEffect(() => {
        const selectedProductObj = draft.selectedProduct || JSON.parse(window.sessionStorage.getItem("selectedProduct"));
        if (selectedProductObj) {
            const sizes = getProductSizes(selectedProductObj);
            const syncedSizes = sizes.map(sizeItem => {
                const existingCartItem = draft.cartItems.find(
                    item => item.sku === sizeItem.sku && item.size === sizeItem.size
                );
                return {
                    ...sizeItem,
                    qty: existingCartItem ? existingCartItem.unitQuantity : 0
                };
            });
            setProducts(syncedSizes);
        }
    }, [draft.selectedProduct, sku, draft.cartItems]);

    const handleClick = (item) => {
        const stepValue = parseInt(qty, 10);
        let newQty = item.qty;

        if (incDec === "increment") {
            newQty = item.qty + stepValue;
        } else if (incDec === "decrement") {
            newQty = Math.max(0, item.qty - stepValue);
        }

        // Update local size items state
        setProducts(prev => prev.map(p => (p.sku === item.sku && p.size === item.size) ? { ...p, qty: newQty } : p));

        // Sync change with draft context (which saves to sessionStorage)
        const selectedProductObj = draft.selectedProduct || JSON.parse(window.sessionStorage.getItem("selectedProduct"));
        updateCartItemQty(selectedProductObj, item, newQty);
    };

    const handleConfirmClear = () => {
        clearCart();
        setProducts(prev => prev.map(p => ({ ...p, qty: 0 })));
        setOpenConfirm(false);
        setNotify({
            isOpen: true,
            message: "Cart cleared successfully",
            type: "success"
        });
    };

    return (
        <div className="mx-4">
            <ResponsiveDrawer heading="Select Size and Quantity - Create Order" />
            <Box
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                }}
                component="main"
            >
                <Toolbar />
                <div className="d-flex justify-content-between m-5 pt-5">
                    <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
                        Back
                    </Button>
                    <div>
                        <h4 className=""> 
                            <span className='fw-bold'>{decodeURIComponent(stylefactory || "")}</span> &nbsp; - &nbsp; {sku}
                        </h4>
                    </div>
                    <div>
                        <Button variant='outlined' color='error' onClick={() => setOpenConfirm(true)}>
                            <DeleteIcon />
                        </Button>
                    </div>
                </div>

                <Grid container spacing={3} sx={{ px: 5 }}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" component="div" fontWeight="bold">
                            Select Size & Quantity
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Please select the size and quantity of product.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1, minWidth: '20px' }}>#</Typography>
                                <ToggleButtonGroup
                                    color="warning"
                                    value={qty}
                                    exclusive
                                    onChange={handleQtyChange}
                                    aria-label="Step select"
                                    size="small"
                                    sx={{
                                        flexWrap: 'wrap',
                                        '& .MuiToggleButton-root.Mui-selected': {
                                            backgroundColor: '#3d3c3cff',
                                            color: 'white',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: '#3d3c3cff',
                                            },
                                        },
                                    }}
                                >
                                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'].map(num => (
                                        <ToggleButton key={num} value={num}>{num}</ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <ToggleButtonGroup
                                    color="warning"
                                    value={incDec}
                                    exclusive
                                    onChange={handleIncDecChange}
                                    aria-label="Increment or decrement mode"
                                    size='medium'
                                    sx={{
                                        '& .MuiToggleButton-root.Mui-selected': {
                                            backgroundColor: '#b93636',
                                            color: 'white',
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: '#b93636',
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="increment"><AddIcon sx={{ mr: 0.5 }} /> Add</ToggleButton>
                                    <ToggleButton value="decrement"><RemoveIcon sx={{ mr: 0.5 }} /> Sub</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Typography variant="h5" component="div">
                            <span className='fw-bold' style={{ color: '#1976d2' }}>{currentProductPairs}</span> Pairs
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <Items items={products} maxHeight='60vh' handleClick={handleClick} />
                </Box>

                {/* Floating Cart Icon with badge count showing total cart quantity */}
                <IconButtonWithBadge count={totalCartPairs} />
            </Box>

            {/* Clear Cart Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Clear Whole Cart?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete all configured quantities and clear the entire cart?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenConfirm(false)} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmClear} variant="contained" color="error">
                        Clear Cart
                    </Button>
                </DialogActions>
            </Dialog>

            <Notification notify={notify} setNotify={setNotify} />
        </div>
    )
}

export default SelectSizeAndQty;